import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  Form,
  Button,
  Input,
  Spin,
  Select,
  Table,
  Switch,
  Popconfirm,
  Modal,
  message,
} from "antd";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import JSZip from "jszip";
import ReactDOM from "react-dom/client";

import { BASE_URL } from "../../utils/constants";
import AdminHeader from "../../components/AdminHeader";
import { showErrorModal } from "../../components/Modal";
import { PageEditor } from "../../components/PageEditor";

import api from "../../api/api";
import { LinkProfile, LinkProfileCreate, UserProfile } from "../../models";
import { useLinkProfile } from "../../api/useLinkProfile";
import { convertTiktokLink } from "../../utils/tiktok";
import CloseCircleFilled from "@ant-design/icons/lib/icons/CloseCircleFilled";
import { EditOutlined } from "@ant-design/icons";
import { useStoreProfile } from "../../api/useStores";

const LinksManager: React.FC = () => {
  const qrCodeSize = 2048;

  const [editingProfile, setEditingProfile] = useState<LinkProfile>({
    name: "main",
  } as LinkProfile);
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  // const [qrCodeSize, setQrCodeSize] = useState(2048);
  const [pageSize] = useState(8);
  const [isAdd, setIsAdd] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isMainLink, setIsMainLink] = useState(false);
  const [showPageEditor, setShowPageEditor] = useState(false);
  const [selectedStore, setSelectedStore] = useState<{
    label: string;
    value: number;
  } | null>();
  const [form] = Form.useForm();

  const {
    page,
    setPage,
    loading,
    linkProfiles,
    getLinkProfiles,
    getLinkProfile,
    addLinkProfile,
  } = useLinkProfile();

  const { storeProfiles, getStoreProfiles, storeLoaded } = useStoreProfile();

  const tblRef: Parameters<typeof Table>[0]["ref"] = React.useRef(null);

  // Detect orientation
  function getOrientation() {
    if (typeof window === "undefined") return "horizontal";
    return window.innerHeight > window.innerWidth ? "vertical" : "horizontal";
  }

  const [vertical, setVertical] = useState(getOrientation() === "vertical");
  useEffect(() => {
    const handleResize = () => {
      setVertical(getOrientation() === "vertical");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchLinkProfiles = (scrollToBottom: boolean = false) => {
    const storeId = selectedStore ? selectedStore.value.toString() : null;
    getLinkProfiles(storeId)
      .then((res) => {
        if (!scrollToBottom) {
          return;
        }

        const totalPages = Math.ceil(res.length / pageSize);
        setPage(totalPages);
        if (tblRef.current) {
          tblRef.current.scrollTo({ index: -1 });
        }
      })
      .catch((err) => {
        console.error("Failed to load links", err);
        message.error("Failed to load links");
      });
  };

  useEffect(() => {
    if (!storeLoaded) {
      return;
    }
    if (storeProfiles.length > 0 && !selectedStore) {
      return;
    }

    fetchLinkProfiles();
  }, [selectedStore, storeLoaded]);

  useEffect(() => {
    getStoreProfiles().then((data) => {
      if (data && data.length > 0) {
        setSelectedStore({ label: data[0].name, value: data[0].id });
      }
    });
  }, []);

  const handleConvertTikTokLinks = async (profile: Partial<LinkProfile>) => {
    if (profile.video_link) {
      const convertedLink = await convertTiktokLink(profile.video_link);
      if (!convertedLink) {
        showErrorModal("Invalid TikTok video link");
        return null;
      } else {
        profile.video_link = convertedLink;
      }
    }

    return profile;
  };

  const handleSubmit = () => {
    var profileToSubmit: LinkProfile = editingProfile;

    if (isMainLink) {
      profileToSubmit.path = "";
    }

    if (selectedStore) {
      profileToSubmit.store_id = selectedStore.value;
    }

    if (isAdd) {
      handleAddLinkProfile(profileToSubmit);
    } else {
      handleEditProfile(profileToSubmit);
    }
  };

  const handleAddLinkProfile = async (linkProfile: LinkProfile) => {
    const convertedProfile = await handleConvertTikTokLinks(linkProfile);
    if (!convertedProfile) {
      return;
    }

    addLinkProfile(convertedProfile as LinkProfileCreate)
      .then(() => {
        fetchLinkProfiles();
        message.success("Link profile added successfully");
        setEditModalVisible(false);
      })
      .catch((err) => {
        console.error("Failed to add link profile", err);
        showErrorModal("Failed to add link profile");
      });
  };

  const handleToggleActive = async (id: number, active: boolean) => {
    try {
      await api.patch(`/v1/link_profile/${id}/`, { active: !active });
      fetchLinkProfiles();
      message.success(
        `Link ${active ? "deactivated" : "activated"} successfully`
      );
    } catch (err) {
      console.error("Failed to update link", err);
      message.error("Failed to update link status");
    }
  };

  const handleDeleteLink = async (id: number) => {
    try {
      await api.delete(`/v1/link_profile/${id}/`);
      fetchLinkProfiles();
      message.success("Link profile deleted successfully");
    } catch (err) {
      console.error("Failed to delete link", err);
      showErrorModal("Failed to delete link profile");
    }
  };

  const handleEditProfile = async (linkProfile: LinkProfile) => {
    try {
      if (!linkProfile) return;

      const convertedProfile = await handleConvertTikTokLinks(linkProfile);
      if (!convertedProfile) {
        return;
      }

      await api.put(
        `/v1/link_profile/${convertedProfile.id}/`,
        convertedProfile
      );
      setEditModalVisible(false);
      fetchLinkProfiles();
      message.success("Link profile updated successfully");
    } catch (err) {
      console.error("Failed to update link", err);
      showErrorModal("Failed to update link profile");
    }
  };

  const showQrCode = (url: string) => {
    setQrCodeUrl(url);
    setQrModalVisible(true);
  };

  const handlePreviewLink = (profile: LinkProfile) => {
    getLinkProfile(profile.id.toString())
      .then((profile) => {
        setEditingProfile(profile);
        form.setFieldsValue(profile);
        setShowPageEditor(true);
      })
      .catch((err) => {
        console.error("Failed to get link profile", err);
        message.error("failed to preview link");
      });
  };

  const handleDuplicateProfile = async (profile: LinkProfile) => {
    if (!profile) {
      message.error("profile are required");
      return;
    }
    const { id, user_profile, ...profileCopy } = profile;
    const maxId = linkProfiles ? Math.max(...linkProfiles.map((p) => p.id)) : 0;

    profileCopy.name = `${profileCopy.name} (${maxId + 1})`;
    profileCopy.path = `${profileCopy.path}-${maxId + 1}`;

    await api
      .post("/v1/link_profile/", profileCopy)
      .then(() => {
        fetchLinkProfiles();
        message.success("Link profile duplicated successfully");
      })
      .catch((err) => {
        console.error("Failed to duplicate link", err);
        showErrorModal("Failed to duplicate link profile");
      });
  };

  const generateQRCodeAsImage = async (
    qrUrl: string,
    filename: string
  ): Promise<Blob> => {
    return new Promise((resolve) => {
      // Create a temporary div to render the QR code
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      document.body.appendChild(tempDiv);

      // Render the QR code
      const qr = (
        <QRCodeCanvas
          value={qrUrl}
          size={qrCodeSize}
          level="H"
          bgColor="#ffffff"
          fgColor="#000000"
          includeMargin={true}
        />
      );

      // Use ReactDOM to render the QR code
      const root = ReactDOM.createRoot(tempDiv);
      root.render(qr);

      // Wait for the QR code to be rendered
      setTimeout(() => {
        const canvas = tempDiv.querySelector("canvas");
        if (canvas) {
          // Create a new canvas for the combined image
          const combinedCanvas = document.createElement("canvas");
          const ctx = combinedCanvas.getContext("2d");
          if (!ctx) return;

          const imgSize = qrCodeSize * 1.25;

          // Set canvas size to accommodate QR code and text
          const padding = 80; // Padding around QR code
          const textHeight = 60; // Height for text
          combinedCanvas.width = imgSize + padding * 2;
          combinedCanvas.height = imgSize + padding * 2 + textHeight;

          // Fill white background
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

          // Draw QR code
          ctx.drawImage(canvas, padding, padding);

          // Draw URL text
          ctx.fillStyle = "#333333";
          ctx.font = "48px Arial";
          ctx.textAlign = "center";

          // Split URL into multiple lines if too long
          const maxWidthQR = combinedCanvas.width - padding * 2;
          const words = qrUrl.split("/");
          let line = "";
          let y = imgSize + padding * 2 + 30; // Starting y position for text

          for (const word of words) {
            const testLine = line + word + "/";
            const metrics = ctx.measureText(testLine);

            if (metrics.width > maxWidthQR && line !== "") {
              ctx.fillText(line, combinedCanvas.width / 2, y);
              line = word + "/";
              y += 30;
            } else {
              line = testLine;
            }
          }
          ctx.fillText(line, combinedCanvas.width / 2, y);

          // Convert to blob
          combinedCanvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            }
            // Clean up
            document.body.removeChild(tempDiv);
          }, "image/png");
        }
      }, 100);
    });
  };

  const downloadAllQRCodes = async () => {
    if (!linkProfiles || linkProfiles.length === 0) {
      message.warning("No links available to generate QR codes");
      return;
    }

    try {
      message.loading({
        content: "Generating QR codes...",
        key: "qrGeneration",
      });

      const zip = new JSZip();
      const qrFolder = zip.folder("qr-codes");

      for (const profile of linkProfiles) {
        let url = getProfileURL(profile.user_profile);
        if (profile.path) {
          url += `/${profile.path}`;
        }

        const qrBlob = await generateQRCodeAsImage(
          url,
          `${profile.path || "main"}.png`
        );
        const filename = `${profile.path || "main"}.png`;
        qrFolder?.file(filename, qrBlob);
      }

      const content = await zip.generateAsync({ type: "blob" });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(content);
      downloadLink.download = "qr-codes.zip";
      downloadLink.click();

      message.success({
        content: "QR codes downloaded successfully!",
        key: "qrGeneration",
      });
    } catch (error) {
      console.error("Error generating QR codes:", error);
      message.error({
        content: "Failed to generate QR codes",
        key: "qrGeneration",
      });
    }
  };

  const getProfileURL = (profile: UserProfile) => {
    let url = `${BASE_URL}/${profile.username}`;
    if (selectedStore) {
      const store = storeProfiles.find(
        (item) => item.id === selectedStore.value
      );
      url = store
        ? `${BASE_URL}/${store.name}`
        : `${BASE_URL}/${profile.username}`;
    } else {
      url = `${BASE_URL}/${profile.username}`;
    }

    return url;
  };

  const columnsMain = [
    {
      title: "Path",
      dataIndex: "path",
      key: "path",
      render: (path: string, item: LinkProfile) => {
        const mainURL = getProfileURL(item.user_profile);
        const fullURL = mainURL;

        return (
          <div className="flex items-center justify-between gap-2 max-w-[400px]">
            <Button
              type="link"
              disabled={!item.active}
              icon={
                <QRCodeSVG
                  style={{ width: "28px", height: "28px" }}
                  value={fullURL}
                  size={28}
                  className="qr-code-svg"
                />
              }
              onClick={(e) => {
                e.preventDefault();
                showQrCode(fullURL);
              }}
            >
              {"(Main link)"}
            </Button>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                getLinkProfile(item.id.toString())
                  .then((profile) => {
                    setEditingProfile(profile);
                    setIsMainLink(profile.path === "");
                    form.setFieldsValue(profile);
                  })
                  .catch((err) => {
                    console.error("error: failed to get link profile", err);
                    setEditingProfile(item);
                    setIsMainLink(item.path === "");
                    form.setFieldsValue(item);
                  })
                  .finally(() => {
                    setIsAdd(false);
                    setEditModalVisible(true);
                  });
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: LinkProfile) => (
        <div
          style={{
            display: "flex",
            flexDirection: vertical ? "column" : "row",
            gap: 4,
          }}
        >
          <Button
            type="link"
            disabled={!record.active}
            onClick={() => {
              handlePreviewLink(record);
            }}
          >
            Preview
          </Button>
        </div>
      ),
    },
  ];

  const columns = [
    {
      title: "Path",
      dataIndex: "path",
      key: "path",
      render: (path: string, item: LinkProfile) => {
        const mainURL = getProfileURL(item.user_profile);
        let fullURL = mainURL;
        if (path) {
          fullURL += `/${path}`;
        }

        return (
          <div className="flex items-center justify-between gap-2 max-w-[400px]">
            <Button
              type="link"
              disabled={!item.active}
              icon={
                <QRCodeSVG
                  style={{ width: "28px", height: "28px" }}
                  value={fullURL}
                  size={28}
                  className="qr-code-svg"
                />
              }
              onClick={(e) => {
                e.preventDefault();
                showQrCode(fullURL);
              }}
            >
              {path ? path : "(Main link)"}
            </Button>

            {/* Edit icon button */}
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                getLinkProfile(item.id.toString())
                  .then((profile) => {
                    setEditingProfile(profile);
                    setIsMainLink(profile.path === "");
                    form.setFieldsValue(profile);
                  })
                  .catch((err) => {
                    console.error("error: failed to get link profile", err);
                    setEditingProfile(item);
                    setIsMainLink(item.path === "");
                    form.setFieldsValue(item);
                  })
                  .finally(() => {
                    setIsAdd(false);
                    setEditModalVisible(true);
                  });
              }}
            />
          </div>
        );
      },
    },
    // {
    //   title: "Full Link",
    //   dataIndex: "path",
    //   key: "id",
    //   hidden: vertical,
    //   render: (path: string, item: LinkProfile) => {
    //     const mainURL = `${BASE_URL}/${item.user_profile.username}`;
    //     let fullURL = mainURL;
    //     if (path) {
    //       fullURL += `/${path}`;
    //     }

    //     return (
    //       <div className="flex items-center justify-between gap-2 max-w-[400px]">
    //         <Button
    //           onClick={(e) => {
    //             e.preventDefault();
    //             showQrCode(fullURL);
    //           }}
    //         >
    //           Gen QR Code
    //         </Button>
    //       </div>
    //     );
    //   },
    // },
    {
      title: "Enabled",
      key: "active",
      render: (record: LinkProfile) => (
        <Switch
          checked={record.active}
          onChange={() => handleToggleActive(record.id, record.active)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: LinkProfile) => (
        <div
          style={{
            display: "flex",
            flexDirection: vertical ? "column" : "row",
            gap: 4,
          }}
        >
          <Button
            type="link"
            disabled={!record.active}
            onClick={() => {
              handlePreviewLink(record);
            }}
          >
            Preview
          </Button>
          <Button
            disabled={record.path === ""}
            type="link"
            onClick={() => {
              handleDuplicateProfile(record);
            }}
          >
            Duplicate
          </Button>
          {/* <Button
            type="link"
            onClick={() => {
              getLinkProfile(record.id.toString())
          .then((profile) => {
            setEditingProfile(profile);
            setIsMainLink(profile.path === "");
            form.setFieldsValue(profile);
          })
          .catch((err) => {
            console.error("error: failed to get link profile", err);
            setEditingProfile(record);
            setIsMainLink(record.path === "");
            form.setFieldsValue(record);
          })
          .finally(() => {
            setIsAdd(false);
            setEditModalVisible(true);
          });
            }}
          >
            Edit
          </Button> */}
          <Popconfirm
            title="Are you sure you want to delete this link?"
            onConfirm={() => handleDeleteLink(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <AdminHeader>
      <HorizontalContainer>
        <MainFrame orientation={getOrientation()}>
          <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <div
              className="px-4 py-6 sm:px-0"
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              <h1 className="text-2xl font-semibold text-gray-900">
                Manage Links
              </h1>

              {selectedStore && (
                <div>
                  <div
                    className={`mt-4 mb-4 gap-2 ${
                      vertical
                        ? "flex items-start"
                        : "flex justify-end items-center"
                    }`}
                    style={{ gap: 10 }}
                  >
                    <span
                      className="font-semibold text-gray-600"
                      style={{ fontSize: 14 }}
                    >
                      Select Store
                    </span>
                    <Select
                      style={{ minWidth: 200 }}
                      placeholder="Select a store"
                      value={selectedStore}
                      options={storeProfiles?.map((store) => ({
                        label: store.name,
                        value: store.id,
                      }))}
                      onChange={(_, option) =>
                        setSelectedStore(
                          option as { label: string; value: number }
                        )
                      }
                    />
                  </div>

                  <Table
                    rowKey="id"
                    loading={loading}
                    columns={columnsMain}
                    dataSource={linkProfiles.filter(
                      (profile) => profile.path === ""
                    )}
                    style={{ maxWidth: "100%" }}
                    pagination={false}
                  />

                  <div
                    className={`mt-4 mb-4 gap-2 ${
                      vertical
                        ? "flex items-start"
                        : "flex justify-end items-center"
                    }`}
                    style={{ gap: 10 }}
                  >
                    <Button
                      type="primary"
                      onClick={() => {
                        setEditingProfile({} as LinkProfile);
                        form.resetFields();
                        setIsAdd(true);
                        setEditModalVisible(true);
                      }}
                    >
                      Add Link
                    </Button>

                    <Button type="primary" onClick={downloadAllQRCodes}>
                      Download All QR Codes
                    </Button>
                  </div>

                  <Table
                    loading={loading}
                    rowKey="id"
                    columns={columns}
                    dataSource={linkProfiles.filter(
                      (profile) => profile.path !== ""
                    )}
                    pagination={{
                      current: page,
                      pageSize: pageSize,
                      total: linkProfiles?.length || 0,
                      showSizeChanger: false,
                      showQuickJumper: false,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    scroll={{
                      x: "max-content",
                      scrollToFirstRowOnChange: false,
                    }}
                    style={{ maxWidth: "100%" }}
                    ref={tblRef}
                    onChange={(pagination) => {
                      setPage(pagination.current || 1);
                      // Scroll to bottom when page changes
                      setTimeout(() => {
                        if (tblRef.current) {
                          tblRef.current.scrollTo({ index: -1 });
                        }
                      }, 100);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <Modal
            title={(isAdd ? "Add" : "Edit") + " Link Profile"}
            open={isEditModalVisible}
            onOk={handleSubmit}
            onCancel={() => {
              setEditModalVisible(false);
            }}
            width={800}
            okButtonProps={{ disabled: !isFormValid }}
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={editingProfile}
              className="grid grid-cols-2 gap-x-6"
              onValuesChange={(_, allValues) =>
                setEditingProfile({ ...editingProfile, ...allValues })
              }
              onFieldsChange={(_, allFields) => {
                const hasError = form
                  .getFieldsError()
                  .some(({ errors }) => errors.length);
                setIsFormValid(!hasError);
              }}
            >
              <Form.Item
                label="Path"
                name="path"
                rules={[{ required: false, message: "Please input the path!" }]}
              >
                <Input id="edit-path" disabled={isMainLink} />
              </Form.Item>

              <Form.Item
                label="Video URL"
                name="video_link"
                rules={[
                  {
                    required: true,
                    message: "Please input the Video link URL!",
                  },
                  {
                    type: "url",
                    message: "Please enter a valid URL!",
                  },
                ]}
              >
                <Input id="edit-video-url" />
              </Form.Item>
              <Form.Item label="Order Button Text" name="guide_text">
                <Input id="edit-guide-text" />
              </Form.Item>
              <Form.Item
                label="Order Button URL"
                name="guide_link"
                rules={[
                  {
                    required: true,
                    message: "Please input the Guide link URL!",
                  },
                  {
                    type: "url",
                    message: "Please enter a valid URL!",
                  },
                ]}
              >
                <Input id="edit-guide-url" />
              </Form.Item>

              {/* <Form.Item label="About Text" name="about_text">
                <Input id="edit-about-text" />
              </Form.Item>
              <Form.Item
                label="About URL"
                name="about_link"
                rules={[
                  {
                    required: true,
                    message: "Please input the About link URL!",
                  },
                  {
                    type: "url",
                    message: "Please enter a valid URL!",
                  },
                ]}
              >
                <Input id="edit-about-url" />
              </Form.Item> */}

              <Form.Item
                label="Link Name"
                name="name"
                style={{ display: "none" }}
              >
                <Input id="edit-name" />
              </Form.Item>
              <Form.Item label="Page Title(Optional)" name="title">
                <Input id="edit-title" />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title="QR Code"
            open={qrModalVisible}
            onCancel={() => setQrModalVisible(false)}
            footer={[
              <Button
                key="download"
                type="primary"
                onClick={() => {
                  const svg = document.querySelector(".qr-code-svg");
                  if (svg) {
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    const img = new Image();
                    img.onload = () => {
                      // Set canvas size to accommodate QR code and text
                      const padding = 80;
                      const textHeight = 60;
                      canvas.width = img.width + padding * 2;
                      canvas.height = img.height + padding * 2 + textHeight;

                      // Fill white background
                      if (ctx) {
                        ctx.fillStyle = "#ffffff";
                        ctx.fillRect(0, 0, canvas.width, canvas.height);

                        // Draw QR code
                        ctx.drawImage(img, padding, padding);

                        // Draw URL text
                        ctx.fillStyle = "#333333";
                        ctx.font = "48px Arial";
                        ctx.textAlign = "center";

                        // Split URL into multiple lines if too long
                        const maxWidthModal = canvas.width - padding * 2;
                        const words = qrCodeUrl.split("/");
                        let line = "";
                        let y = img.height + padding * 2 + 30;

                        for (const word of words) {
                          const testLine = line + word + "/";
                          const metrics = ctx.measureText(testLine);

                          if (metrics.width > maxWidthModal && line !== "") {
                            ctx.fillText(line, canvas.width / 2, y);
                            line = word + "/";
                            y += 30;
                          } else {
                            line = testLine;
                          }
                        }
                        ctx.fillText(line, canvas.width / 2, y);
                      }

                      const pngFile = canvas.toDataURL("image/png");
                      const downloadLink = document.createElement("a");
                      downloadLink.download = `${qrCodeUrl}.png`;
                      downloadLink.href = pngFile;
                      downloadLink.click();
                    };
                    img.src = "data:image/svg+xml;base64," + btoa(svgData);
                  }
                }}
              >
                Download QR Code
              </Button>,
              <Button key="close" onClick={() => setQrModalVisible(false)}>
                Close
              </Button>,
            ]}
          >
            <div className="flex justify-center p-4">
              <QRCodeSVG
                style={{ width: "512px", height: "512px" }}
                value={qrCodeUrl}
                size={qrCodeSize}
                className="qr-code-svg"
              />
            </div>
            <div className="text-center mt-4">
              <a href={qrCodeUrl} className="text-gray-600 break-all underline">
                {qrCodeUrl}
              </a>
            </div>
          </Modal>
        </MainFrame>
        {showPageEditor && (
          <PhoneMockup orientation={getOrientation()}>
            <div className="screen" style={{ position: "relative" }}>
              <CloseCircleFilled
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  zIndex: 10,
                  width: 32,
                  height: 32,
                  fontSize: 32,
                  cursor: "pointer",
                  borderRadius: "50%",
                  border: "none",
                  background: "transparent",
                  color: "#CCC",
                }}
                onClick={() => setShowPageEditor(false)}
                aria-label="Close"
              />
              <PageEditor profileIn={editingProfile} />
            </div>
          </PhoneMockup>
        )}
      </HorizontalContainer>
    </AdminHeader>
  );
};

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 80vh;
`;

const MainFrame = styled.div<{ orientation: string }>`
  width: ${(props) => (props.orientation === "vertical" ? "100%" : "70%")};
  flex-direction: column;
  justify-content: center;
`;

const PhoneMockup = styled.div<{ orientation: string }>`
  width: ${(props) => (props.orientation === "vertical" ? "100%" : "30%")};
  position: ${(props) =>
    props.orientation === "vertical" ? "absolute" : "relative"};
  margin-top: 80px;
  padding: 1rem;
  max-height: 1000px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 30px;
    border: 12px solid #000;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    pointer-events: none;
    z-index: 1;
  }

  .screen {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 100%;
    max-height: 970px;
    background: #fff;
    border-radius: 18px;
    overflow: hidden; /* Disable scroll */
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 70vh;
  }
`;

export default LinksManager;
